import {
  Button,
  DateInput,
  deleteProps,
  formatDate,
  TextInput,
} from "@inceptionbg/ui-components";
import { FC, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IInputComponentProps } from "../../../Elements/Input";
import { Form } from "../../Wrapper/FormWrapper";
import { ILand, ILandNew, ILandUsageBasis } from "../../../types/ILand";
import {
  calculateSurfaceFromObj,
  convertSurfaceToObj,
} from "../../../utils/CalculateUtils";
import { deleteLand, editLand } from "../../../repos/LandRepo";
import { SurfaceValue } from "../../Inputs/Surface/SurfaceValue";
import { SurfaceInput } from "../../Inputs/Surface/SurfaceInput";
import { EditLandEvidenceDialog } from "../Land/EditLandEvidenceDialog";
import { Dialog } from "../../../Elements/Dialog";
import { ModeContext } from "../../../Pages/RPGPage";
import { inputPattern } from "../../../utils/InputPatternValidation";
import { FarmContext } from "../../../AppContent";
import { ReplaceLandDialog } from "../Land/ReplaceLandDialog";
import { PartLandEvidenceDialog } from "../Land/PartLandEvidenceDialog";
import { setIsUserProcessing } from "../../../repos/FarmRepo";
import { deleteFieldLand } from "../../../repos/FIeldBookRepo";
import { IEvidence } from "../../../types/IFile";

interface Props {
  land: ILand;
  newForm?: {
    onCancel: () => void;
  };
  reloadLands: (callback?: () => void) => void;
  reloadLandsSum?: () => void;
  membersLand?: boolean;
  approvalUuid?: string;
  disableEdit?: boolean;
  fieldLandUuid?: string;
}

export const LandForm: FC<Props> = ({
  land,
  newForm,
  reloadLands,
  reloadLandsSum,
  membersLand,
  approvalUuid,
  disableEdit = false,
  fieldLandUuid,
}) => {
  const [formData, setFormData] = useState<ILandNew>({});
  const [edit, setEdit] = useState(false);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [isReplaceLandOpen, setIsReplaceLandOpen] = useState(false);
  const [landToDeleteUuid, setLandToDeleteUuid] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenLandPartDialog, setIsOpenLandPartDialog] = useState(false);
  const [isInApproval, setIsInApproval] = useState(false);
  const [shouldSubmitAfterEvidence, setShouldSubmitAfterEvidence] =
    useState(false);
  const [isEditingLeaseEndDate, setIsEditingLeaseEndDate] = useState(false);

  const { t } = useTranslation();
  const { farm, isFarmInApproval } = useContext(FarmContext);
  const isRenew = useContext(ModeContext) === "RENEW";
  const isUserProcessingFarm = !!farm?.registrationApproval?.userProcessing;
  const isUserProcessing = !!land.approval?.userProcessing;

  const surfaceObj = convertSurfaceToObj(land.surface);
  const usedSurfaceObj = convertSurfaceToObj(land.usedSurface);

  const disableFarmAdministrativeProcedureEdit =
    !!farm?.registrationApproval?.administrativeProcedure &&
    farm?.registrationApproval?.approvalStatus !== "WAITING_COMPLEMENT";
  const disableFormEdit =
    disableEdit ||
    land.approval?.adminProcessing === true ||
    land.approval?.approvalStatus === "DECISION_SEND" ||
    (isInApproval &&
      land.approval?.administrativeProcedure === true &&
      land.approval?.approvalStatus !== "WAITING_COMPLEMENT") ||
    disableFarmAdministrativeProcedureEdit;
  const isWaitingComplement =
    land.approval?.approvalStatus === "WAITING_COMPLEMENT" ||
    farm?.registrationApproval?.approvalStatus === "WAITING_COMPLEMENT";

  const checkIsInLease = (e?: ILandUsageBasis | "UPZ") => {
    return [
      "LEASE",
      "LEASE_CONSOLIDATION",
      "LEASE_LEGAL_ENTITY",
      "LEASE_LEGAL_ENTITY_CONSOLIDATION",
    ].includes(e || "");
  };

  // eslint-disable-next-line
  useEffect(() => setFormData({ ...land, surfaceObj, usedSurfaceObj }), [land]);

  useEffect(() => {
    setIsInApproval(isFarmInApproval || (!land.approved && !!land.approval));
  }, [land, isFarmInApproval]);

  useEffect(() => {
    if (isUserProcessing) {
      setEdit(true);
      setIsInApproval(false);
    }
  }, [isUserProcessing]);

  useEffect(() => {
    if (edit && checkIsInLease(land.usageBasis) && !isFarmInApproval) {
      const leaseEndDateChanged = formData.leaseEndDate !== land.leaseEndDate;
      const otherFieldsChanged =
        formData.nickname !== land.nickname ||
        formData.usedSurface !== land.usedSurface ||
        formData.lessorIdentification !== land.lessorIdentification;

      setIsEditingLeaseEndDate(leaseEndDateChanged && !otherFieldsChanged);
    } else {
      setIsEditingLeaseEndDate(false);
    }
  }, [edit, formData, land, isFarmInApproval]);

  const landInfo: IInputComponentProps[] = [
    {
      label: t("LandName"),
      value: land.nickname,
      input: (
        <TextInput
          value={formData.nickname}
          setValue={(e) => setFormData({ ...formData, nickname: e })}
          inputProps={{ pattern: inputPattern.maxChar255 }}
          disabled={
            edit &&
            checkIsInLease(land.usageBasis) &&
            !isFarmInApproval &&
            isEditingLeaseEndDate
          }
        />
      ),
    },
    {
      label: t("CadastreMunicipality"),
      value: land.cadastreMunicipality.name,
      readOnly: true,
    },
    {
      label: t("CadastreNumber"),
      value: land.cadastreNumber,
      readOnly: true,
    },
    {
      label: t("PartLand"),
      valueElement: (
        <Button
          label={t("ShowLandPartEvidence")}
          outlined
          size="s"
          onClick={() => setIsOpenLandPartDialog(true)}
        />
      ),
      hidden: formData.usageBasis === "CONSOLIDATION",
    },
    {
      label: t("Surface"),
      valueElement: <SurfaceValue valueObj={surfaceObj} />,
      readOnly: true,
    },
    {
      label: t("UsageBasis"),
      value: land.usageBasis
        ? t(land.usageBasis) +
          (land.member
            ? ` (${land.member.firstName} ${land.member.lastName})`
            : "")
        : "/",
      readOnly: true,
      // hidden: formData.usageBasis === "LEASE",
    },
    {
      label: t("UsageBasisUPZ"),
      value: land.upzBase?.name || "/",
      readOnly: true,
      hidden: formData.usageBasis !== "UPZ",
    },
    {
      label: t("LeaseEndDate"),
      value: formatDate(land.leaseEndDate),
      input: (
        <DateInput
          required
          date={formData.leaseEndDate}
          setDate={(e) => setFormData({ ...formData, leaseEndDate: e })}
          error={!formData.leaseEndDate}
          disabled={
            edit &&
            checkIsInLease(land.usageBasis) &&
            !isFarmInApproval &&
            !isEditingLeaseEndDate &&
            (formData.nickname !== land.nickname ||
              formData.usedSurface !== land.usedSurface ||
              formData.lessorIdentification !== land.lessorIdentification)
          }
        />
      ),
      readOnly: isFarmInApproval,
      hidden: !checkIsInLease(formData?.usageBasis),
      alert: {
        text: t("RequiredFieldError"),
        type: "error",
        active: !formData.leaseEndDate,
      },
    },
    {
      label: t("UsedSurface"),
      valueElement: <SurfaceValue valueObj={usedSurfaceObj} />,
      readOnly:
        !!formData.usedSurface ||
        (edit &&
          checkIsInLease(land.usageBasis) &&
          !isFarmInApproval &&
          isEditingLeaseEndDate),
      input: (
        <SurfaceInput
          formData={formData}
          setFormData={setFormData}
          value="usedSurfaceObj"
          required
          error={
            !!formData.usedSurfaceObj &&
            !calculateSurfaceFromObj(formData.usedSurfaceObj)
          }
        />
      ),
      alert: {
        text: t("RequiredFieldError"),
        type: "error",
        active:
          !!formData.usedSurfaceObj &&
          !calculateSurfaceFromObj(formData.usedSurfaceObj),
      },
    },
    {
      label: t("LeaseType"),
      value: formData.leaseType
        ? `${formData.leaseType.code} - ${formData.leaseType.name}`
        : "/",
      hidden: !checkIsInLease(formData?.usageBasis),
    },
    {
      label: t("LessorIdentificationNumber"),
      value: formData.lessorIdentification,
      input: (
        <TextInput
          required
          value={formData.lessorIdentification}
          setValue={(e) =>
            setFormData({ ...formData, lessorIdentification: e })
          }
          inputProps={{
            pattern: inputPattern.registrationOrPersonalIdentityNumber,
          }}
          error={!formData.lessorIdentification}
          disabled={
            edit &&
            checkIsInLease(land.usageBasis) &&
            !isFarmInApproval &&
            isEditingLeaseEndDate
          }
        />
      ),
      readOnly: isFarmInApproval,
      hidden: !checkIsInLease(formData?.usageBasis),
      alert: {
        text: t("RequiredFieldError"),
        type: "error",
        active: !formData.lessorIdentification,
      },
    },
  ];

  const onSubmit = useCallback(
    (evidences?: IEvidence[], hasUploadedEvidence = false) => {
      setIsLoading(true);
      const data: ILandNew = deleteProps(
        {
          ...formData,
          surface: formData.surfaceObj
            ? calculateSurfaceFromObj(formData.surfaceObj)
            : formData.surface,
          usedSurface: formData.usedSurfaceObj
            ? calculateSurfaceFromObj(formData.usedSurfaceObj)
            : formData.usedSurface,
        },
        ["surfaceObj", "usedSurfaceObj"]
      );

      if (!!data.uuid) {
        const isLeaseEndDateChange =
          checkIsInLease(data.usageBasis) &&
          !isFarmInApproval &&
          data.leaseEndDate !== land.leaseEndDate;

        const payload: ILandNew = isLeaseEndDateChange
          ? {
              leaseEndDate: data.leaseEndDate,
              evidences: evidences || undefined,
            }
          : {
              ...(data.usedSurface !== land.usedSurface && {
                usedSurface: data.usedSurface,
              }),
              ...(data.nickname !== land.nickname && {
                nickname: data.nickname,
              }),
              ...(!isFarmInApproval &&
                data.lessorIdentification !== land.lessorIdentification && {
                  lessorIdentification: data.lessorIdentification,
                }),
            };

        editLand(data.uuid, payload)
          .then(() => {
            if (!hasUploadedEvidence && land.approval?.uuid) {
              return setIsUserProcessing(land.approval.uuid, false).then(() => {
                if (!!reloadLands) {
                  reloadLands(() => {
                    setIsLoading(false);
                    setEdit(false);
                    newForm && newForm.onCancel();
                  });
                } else {
                  setIsLoading(false);
                  setEdit(false);
                }
              });
            } else {
              if (!!reloadLands) {
                reloadLands(() => {
                  setIsLoading(false);
                  setEdit(false);
                  newForm && newForm.onCancel();
                });
              } else {
                setIsLoading(false);
                setEdit(false);
              }
            }
          })
          .catch(() => setIsLoading(false));
      }
    },
    [formData, isFarmInApproval, reloadLands, newForm, land]
  );

  const handleStartEdit = () => {
    if (!isFarmInApproval && land?.approval?.uuid) {
      setIsLoading(true);
      setIsUserProcessing(land.approval.uuid, true)
        .then(() => {
          setIsInApproval(false);
          setEdit(true);
          setIsLoading(false);
        })
        .catch(() => setIsLoading(false));
    } else {
      setEdit(true);
    }
  };

  const onDelete = () => {
    if (landToDeleteUuid) {
      setIsLoading(true);
      if (!!fieldLandUuid) {
        deleteFieldLand(landToDeleteUuid)
          .then(() => !!reloadLandsSum && reloadLandsSum())
          .catch(() => {})
          .finally(() => setIsLoading(false));
      } else {
        deleteLand(landToDeleteUuid)
          .then(() => {
            if (!!reloadLands) {
              reloadLands(() => {
                setIsLoading(false);
                setEdit(false);
                newForm && newForm.onCancel();
              });
            } else {
              setIsLoading(false);
              setEdit(false);
            }
            !!reloadLandsSum && reloadLandsSum();
          })
          .catch(() => setIsLoading(false));
      }
    }
  };

  const handleSubmit = () => {
    const leaseEndDateChanged =
      !isFarmInApproval &&
      checkIsInLease(land.usageBasis) &&
      formData.leaseEndDate !== land.leaseEndDate;

    if (leaseEndDateChanged) {
      setShouldSubmitAfterEvidence(true);
      setEvidenceOpen(true);
    } else {
      onSubmit();
      setEdit(false);
    }
  };

  const handleEvidenceDialogConfirm = useCallback(
    (evidences: IEvidence[], hasUploadedEvidence: boolean) => {
      if (shouldSubmitAfterEvidence) {
        onSubmit(evidences, hasUploadedEvidence);
        setShouldSubmitAfterEvidence(false);
        setEdit(false);
      } else {
        if (reloadLands) {
          reloadLands();
        }
      }
    },
    [shouldSubmitAfterEvidence, reloadLands, onSubmit]
  );

  return (
    <>
      <Form
        data={landInfo}
        edit={!!newForm || edit}
        setEdit={setEdit}
        submitButton={{
          editLabel: t(!!newForm || edit ? "Confirm" : "LandEdit"),
          onSubmit: !!newForm || edit ? handleSubmit : handleStartEdit,
          callOnSubmitWhenNotEdit: true,
          hidden:
            disableFormEdit ||
            (isFarmInApproval && !isUserProcessingFarm && !newForm),
        }}
        otherButtons={[
          {
            label: t("EndChanges"),
            onClick: () =>
              setIsUserProcessing(land?.approval?.uuid!, false)
                .then(() => setEdit(false))
                .catch(() => {}),
            outlined: true,
            hidden:
              !land?.approval?.uuid ||
              isInApproval ||
              !edit ||
              disableFormEdit ||
              !!newForm,
          },
          {
            label: t("DeleteLand"),
            onClick: () => setLandToDeleteUuid(formData.uuid || ""),
            hidden:
              (!edit &&
                (farm?.status === "PASSIVE" ||
                  farm?.registrationApproval?.approvalStatus !==
                    "WAITING_COMPLEMENT")) ||
              (isInApproval && !isFarmInApproval) ||
              disableFormEdit ||
              formData.usageBasis === "UPZ",
            outlined: true,
            className: "error",
          },
          {
            label: t("DeleteFieldLand"),
            onClick: () => setLandToDeleteUuid(fieldLandUuid || ""),
            hidden: !fieldLandUuid,
            outlined: true,
            className: "error",
          },
          {
            label: t("Attachments"),
            primary: true,
            onClick: () => setEvidenceOpen(true),
            hidden:
              land.usageBasis === "CADASTRE_OWNERSHIP" ||
              !edit ||
              isRenew ||
              isInApproval ||
              disableFormEdit ||
              formData.usageBasis === "UPZ",
          },
          {
            label: t("ReplaceLand"),
            primary: true,
            onClick: () => setIsReplaceLandOpen(true),
            hidden:
              isInApproval ||
              disableFormEdit ||
              !isWaitingComplement ||
              formData.usageBasis === "UPZ",
          },
        ]}
        buttonSize={membersLand ? "xs" : "s"}
        onCancel={!!newForm ? newForm.onCancel : undefined}
        onResetForm={() => setFormData({ ...land, surfaceObj, usedSurfaceObj })}
        isLoading={isLoading}
      />
      {isWaitingComplement && (
        <ReplaceLandDialog
          isOpen={isReplaceLandOpen}
          onClose={() => setIsReplaceLandOpen(false)}
          land={land}
          reloadLands={reloadLands}
        />
      )}
      {!isRenew && (
        <>
          <EditLandEvidenceDialog
            isOpen={evidenceOpen}
            onClose={() => {
              setEvidenceOpen(false);
              setShouldSubmitAfterEvidence(false);
            }}
            landUuid={land.uuid}
            approvalUuid={approvalUuid}
            reloadLands={reloadLands}
            onConfirm={handleEvidenceDialogConfirm}
            onEvidenceUploaded={() => setEdit(false)}
            // membersLand={membersLand}
            // usageBasis={land.usageBasis}
            complementApproval={
              land.approval?.administrativeProcedure === true &&
              land.approval?.approvalStatus === "WAITING_COMPLEMENT"
                ? false
                : undefined
            }
          />
          <Dialog
            isOpen={!!landToDeleteUuid}
            onClose={() => setLandToDeleteUuid("")}
            title={t("DeleteLand")}
            desc={t(!!fieldLandUuid ? "DeleteFieldLandDesc" : "DeleteLandDesc")}
            isLoading={isLoading}
            confirmButton={{ label: t("Confirm"), onClick: onDelete }}
            cancelButton={{ onClick: () => setLandToDeleteUuid("") }}
            error
          />
        </>
      )}
      {isOpenLandPartDialog && (
        <PartLandEvidenceDialog
          land={land}
          isOpen={isOpenLandPartDialog}
          onClose={() => setIsOpenLandPartDialog(false)}
        />
      )}
    </>
  );
};
